import { useAppSelector } from "app/store";
import { useMemo } from "react";
import { useSearchTasksQuery } from "./endpoints";
import { transformPropertyFilterQuery } from "components/server-side-table/propertyFilterTransform";
import { getFilterForStat } from "./tasksStatFilter";
import { SearchScope } from "./definitions";
import type { TaskFilteringProperty } from "./filteringProperties";
import { GaiaConstants } from "utils";

export const useUserNameSelector = () => {
  const userName = useAppSelector((state) => state.auth.user.alias);
  return { userName };
};

export const useTeamNameSelector = () => {
  const teamName = useAppSelector((state) => state.audits.teamName);
  return { teamName };
};
export { useAppSelector };

interface UseTaskStatsParams {
  teamName: string | undefined;
  currentUser: string | undefined;
  filteringProperties: TaskFilteringProperty[];
  showAgentWork?: boolean;
}

interface StatItem {
  count: number;
  isLoading: boolean;
}

interface TaskStatsResult {
  stats: {
    myOpenTasksCount: StatItem;
    myTasksOverdueCount: StatItem;
    myTasksDueIn30DaysCount: StatItem;
    myTasksDueIn7DaysCount: StatItem;
    myAgentNeedsInputCount: StatItem;
    myTeamTasksOverdueCount: StatItem;
    myTeamTasksDueIn30DaysCount: StatItem;
    myTeamTasksDueIn7DaysCount: StatItem;
    myTeamOpenTasksCount: StatItem;
    myTeamGuestOwnedOpenTasksCount: StatItem;
    myTeamOpenTasksAssignedToInactiveMembersCount: StatItem;
    teamMemberAsGuestOpenTasksCount: StatItem;
    teamMemberAsGuestOpenTasksOverdueCount: StatItem;
    teamMemberAsGuestOpenTasksDueIn30DaysCount: StatItem;
    teamMemberAsGuestOpenTasksDueIn7DaysCount: StatItem;
    teamQueueAllCount: StatItem;
    teamQueueOverdueCount: StatItem;
    teamQueueDueIn7DaysCount: StatItem;
    teamQueueDueIn30DaysCount: StatItem;
  };
  metadata: {
    guests: string[];
    inactiveMembers: string[];
  };
}

/**
 * Custom hook to fetch task statistics for gaia_dev team using OpenSearch.
 * Implements two-batch loading:
 * - Batch 1: 6 metadata-independent queries (fires immediately)
 * - Batch 2: 5 metadata-dependent queries (fires after metadata is ready)
 */
export const useTaskStats = ({
  teamName,
  currentUser,
  filteringProperties,
  showAgentWork = false,
}: UseTaskStatsParams): TaskStatsResult => {
  // ============================================================================
  // BATCH 1: Metadata-independent queries (6 queries)
  // ============================================================================
  const isGuest = teamName === GaiaConstants.GUEST_TEAM;
  const shouldFetchBatch1 = teamName && !!currentUser;

  // Query 1: myOpenTasksCount - WITH metadata
  const myOpenTasksQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch1
        ? transformPropertyFilterQuery(
            getFilterForStat("myOpenTasks", currentUser, teamName),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: !isGuest, // Only this one fetches metadata and it's only for non-guest team
      countOnly: true,
      searchScope: "ASSIGNED_TO_ME" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 2: myTasksOverdueCount
  const myTasksOverdueQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch1
        ? transformPropertyFilterQuery(
            getFilterForStat("myTasksOverdue", currentUser, teamName),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "ASSIGNED_TO_ME" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 3: myTasksDueIn7DaysCount
  const myTasksDueIn7DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch1
        ? transformPropertyFilterQuery(
            getFilterForStat("myTasksDueIn7Days", currentUser, teamName),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "ASSIGNED_TO_ME" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 4: myTasksDueIn30DaysCount
  const myTasksDueIn30DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch1
        ? transformPropertyFilterQuery(
            getFilterForStat("myTasksDueIn30Days", currentUser, teamName),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "ASSIGNED_TO_ME" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  const shouldFetchAgentStat = shouldFetchBatch1 && showAgentWork;
  const myAgentNeedsInputQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchAgentStat
        ? transformPropertyFilterQuery(
            getFilterForStat("myAgentNeedsInput", currentUser, teamName),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "ASSIGNED_TO_ME" as SearchScope,
    },
    {
      skip: !shouldFetchAgentStat,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 5: myTeamTasksOverdueCount
  const myTeamTasksOverdueQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("myTeamTasksOverdue", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 6: myTeamTasksDueIn7DaysCount
  const myTeamTasksDueIn7DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("myTeamTasksDueIn7Days", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 7: myTeamTasksDueIn30DaysCount
  const myTeamTasksDueIn30DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("myTeamTasksDueIn30Days", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 8: myTeamOpenTasksCount
  const myTeamOpenTasksQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("myTeamOpenTasks", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 9: teamQueueAllCount
  const teamQueueAllQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("teamQueueAll", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "TEAM_QUEUE" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 10: teamQueueOverdueCount
  const teamQueueOverdueQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("teamQueueOverdue", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "TEAM_QUEUE" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 11: teamQueueDueIn7DaysCount
  const teamQueueDueIn7DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("teamQueueDueIn7Days", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "TEAM_QUEUE" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 12: teamQueueDueIn30DaysCount
  const teamQueueDueIn30DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery:
        shouldFetchBatch1 && !isGuest
          ? transformPropertyFilterQuery(
              getFilterForStat("teamQueueDueIn30Days", currentUser, teamName),
              filteringProperties,
            )
          : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "TEAM_QUEUE" as SearchScope,
    },
    {
      skip: !shouldFetchBatch1,
      refetchOnMountOrArgChange: true,
    },
  );

  // Extract metadata from first query
  const metadata = useMemo(() => {
    const metadataResult = myOpenTasksQuery.data?.metadata;
    if (!metadataResult) {
      return { guests: [], inactiveMembers: [] };
    }
    return {
      guests: metadataResult.guests || [],
      inactiveMembers: metadataResult.inactiveMembers || [],
    };
  }, [myOpenTasksQuery.data?.metadata]);

  // ============================================================================
  // BATCH 2: Metadata-dependent queries (5 queries)
  // ============================================================================
  const shouldFetchBatch2 =
    shouldFetchBatch1 && !isGuest && !!myOpenTasksQuery.data?.metadata;

  // Query 9: myTeamGuestOwnedOpenTasksCount
  const myTeamGuestOwnedOpenTasksQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "myTeamGuestOwnedOpenTasks",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 10: myTeamOpenTasksAssignedToInactiveMembersCount
  const myTeamOpenTasksAssignedToInactiveMembersQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "myTeamOpenTasksAssignedToInactiveMembers",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "MY_TEAM" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 11: teamMemberAsGuestOpenTasksCount
  const teamMemberAsGuestOpenTasksQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "teamMemberAsGuestOpenTasks",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "GUEST_ASSIGNMENTS" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 12: teamMemberAsGuestOpenTasksOverdueCount
  const teamMemberAsGuestOpenTasksOverdueQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "teamMemberAsGuestOpenTasksOverdue",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "GUEST_ASSIGNMENTS" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 13: teamMemberAsGuestOpenTasksDueIn7DaysCount
  const teamMemberAsGuestOpenTasksDueIn7DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "teamMemberAsGuestOpenTasksDueIn7Days",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "GUEST_ASSIGNMENTS" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Query 14: teamMemberAsGuestOpenTasksDueIn30DaysCount
  const teamMemberAsGuestOpenTasksDueIn30DaysQuery = useSearchTasksQuery(
    {
      teamName: teamName || "",
      propertyFilterQuery: shouldFetchBatch2
        ? transformPropertyFilterQuery(
            getFilterForStat(
              "teamMemberAsGuestOpenTasksDueIn30Days",
              currentUser,
              teamName,
              metadata.guests,
              metadata.inactiveMembers,
            ),
            filteringProperties,
          )
        : undefined,
      page: 1,
      pageSize: 0,
      includeMetadata: false,
      countOnly: true,
      searchScope: "GUEST_ASSIGNMENTS" as SearchScope,
    },
    {
      skip: !shouldFetchBatch2,
      refetchOnMountOrArgChange: true,
    },
  );

  // Aggregate counts and loading states together
  const stats = useMemo(
    () => ({
      myOpenTasksCount: {
        count: myOpenTasksQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myOpenTasksQuery.isLoading,
      },
      myTasksOverdueCount: {
        count: myTasksOverdueQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTasksOverdueQuery.isLoading,
      },
      myTasksDueIn30DaysCount: {
        count:
          myTasksDueIn30DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTasksDueIn30DaysQuery.isLoading,
      },
      myTasksDueIn7DaysCount: {
        count: myTasksDueIn7DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTasksDueIn7DaysQuery.isLoading,
      },
      myAgentNeedsInputCount: {
        count: myAgentNeedsInputQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myAgentNeedsInputQuery.isLoading,
      },
      myTeamTasksOverdueCount: {
        count:
          myTeamTasksOverdueQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTeamTasksOverdueQuery.isLoading,
      },
      myTeamTasksDueIn30DaysCount: {
        count:
          myTeamTasksDueIn30DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTeamTasksDueIn30DaysQuery.isLoading,
      },
      myTeamTasksDueIn7DaysCount: {
        count:
          myTeamTasksDueIn7DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTeamTasksDueIn7DaysQuery.isLoading,
      },
      myTeamOpenTasksCount: {
        count: myTeamOpenTasksQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: myTeamOpenTasksQuery.isLoading,
      },
      myTeamGuestOwnedOpenTasksCount: {
        count:
          myTeamGuestOwnedOpenTasksQuery.data?.paginationMetadata?.totalCount ??
          0,
        isLoading: myTeamGuestOwnedOpenTasksQuery.isLoading,
      },
      myTeamOpenTasksAssignedToInactiveMembersCount: {
        count:
          myTeamOpenTasksAssignedToInactiveMembersQuery.data?.paginationMetadata
            ?.totalCount ?? 0,
        isLoading: myTeamOpenTasksAssignedToInactiveMembersQuery.isLoading,
      },
      teamMemberAsGuestOpenTasksCount: {
        count:
          teamMemberAsGuestOpenTasksQuery.data?.paginationMetadata
            ?.totalCount ?? 0,
        isLoading: teamMemberAsGuestOpenTasksQuery.isLoading,
      },
      teamMemberAsGuestOpenTasksOverdueCount: {
        count:
          teamMemberAsGuestOpenTasksOverdueQuery.data?.paginationMetadata
            ?.totalCount ?? 0,
        isLoading: teamMemberAsGuestOpenTasksOverdueQuery.isLoading,
      },
      teamMemberAsGuestOpenTasksDueIn30DaysCount: {
        count:
          teamMemberAsGuestOpenTasksDueIn30DaysQuery.data?.paginationMetadata
            ?.totalCount ?? 0,
        isLoading: teamMemberAsGuestOpenTasksDueIn30DaysQuery.isLoading,
      },
      teamMemberAsGuestOpenTasksDueIn7DaysCount: {
        count:
          teamMemberAsGuestOpenTasksDueIn7DaysQuery.data?.paginationMetadata
            ?.totalCount ?? 0,
        isLoading: teamMemberAsGuestOpenTasksDueIn7DaysQuery.isLoading,
      },
      teamQueueAllCount: {
        count: teamQueueAllQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: teamQueueAllQuery.isLoading,
      },
      teamQueueOverdueCount: {
        count: teamQueueOverdueQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: teamQueueOverdueQuery.isLoading,
      },
      teamQueueDueIn7DaysCount: {
        count:
          teamQueueDueIn7DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: teamQueueDueIn7DaysQuery.isLoading,
      },
      teamQueueDueIn30DaysCount: {
        count:
          teamQueueDueIn30DaysQuery.data?.paginationMetadata?.totalCount ?? 0,
        isLoading: teamQueueDueIn30DaysQuery.isLoading,
      },
    }),
    [
      myOpenTasksQuery.data,
      myOpenTasksQuery.isLoading,
      myTasksOverdueQuery.data,
      myTasksOverdueQuery.isLoading,
      myTasksDueIn30DaysQuery.data,
      myTasksDueIn30DaysQuery.isLoading,
      myTasksDueIn7DaysQuery.data,
      myTasksDueIn7DaysQuery.isLoading,
      myAgentNeedsInputQuery.data,
      myAgentNeedsInputQuery.isLoading,
      myTeamTasksOverdueQuery.data,
      myTeamTasksOverdueQuery.isLoading,
      myTeamTasksDueIn30DaysQuery.data,
      myTeamTasksDueIn30DaysQuery.isLoading,
      myTeamTasksDueIn7DaysQuery.data,
      myTeamTasksDueIn7DaysQuery.isLoading,
      myTeamOpenTasksQuery.data,
      myTeamOpenTasksQuery.isLoading,
      myTeamGuestOwnedOpenTasksQuery.data,
      myTeamGuestOwnedOpenTasksQuery.isLoading,
      myTeamOpenTasksAssignedToInactiveMembersQuery.data,
      myTeamOpenTasksAssignedToInactiveMembersQuery.isLoading,
      teamMemberAsGuestOpenTasksQuery.data,
      teamMemberAsGuestOpenTasksQuery.isLoading,
      teamMemberAsGuestOpenTasksOverdueQuery.data,
      teamMemberAsGuestOpenTasksOverdueQuery.isLoading,
      teamMemberAsGuestOpenTasksDueIn30DaysQuery.data,
      teamMemberAsGuestOpenTasksDueIn30DaysQuery.isLoading,
      teamMemberAsGuestOpenTasksDueIn7DaysQuery.data,
      teamMemberAsGuestOpenTasksDueIn7DaysQuery.isLoading,
      teamQueueAllQuery.data,
      teamQueueAllQuery.isLoading,
      teamQueueOverdueQuery.data,
      teamQueueOverdueQuery.isLoading,
      teamQueueDueIn7DaysQuery.data,
      teamQueueDueIn7DaysQuery.isLoading,
      teamQueueDueIn30DaysQuery.data,
      teamQueueDueIn30DaysQuery.isLoading,
    ],
  );

  return {
    stats,
    metadata,
  };
};
